import * as THREE from 'three';
import { toCreasedNormals } from 'three/addons/utils/BufferGeometryUtils.js';
import type { ManifoldToplevel } from 'manifold-3d';

export interface ManifoldResult {
  geometry: THREE.BufferGeometry;
  vertexCount: number;
  faceCount: number;
  timeMs: number;
}

export class ManifoldEngine {
  private wasm: ManifoldToplevel | null = null;
  refineLevel: number = 1;
  segments: number = 32;

  async init(): Promise<void> {
    const Module = (await import('manifold-3d')).default;
    this.wasm = await Module();
    this.wasm.setup();
  }

  isReady(): boolean {
    return this.wasm !== null;
  }

  getWasm(): ManifoldToplevel {
    if (!this.wasm) {
      throw new Error('ManifoldEngine not initialized. Call init() first.');
    }
    return this.wasm;
  }

  evaluate(
    builder: (wasm: ManifoldToplevel, segments: number) => InstanceType<ManifoldToplevel['Manifold']>,
  ): ManifoldResult {
    const wasm = this.getWasm();

    const startTime = performance.now();
    let manifold = builder(wasm, this.segments);

    if (this.refineLevel > 1) {
      const refined = manifold.refine(this.refineLevel);
      manifold.delete();
      manifold = refined;
    }

    const geometry = manifoldToGeometry(manifold);
    const timeMs = performance.now() - startTime;

    const posAttr = geometry.getAttribute('position');
    const indexAttr = geometry.getIndex();
    const vertexCount = posAttr ? posAttr.count : 0;
    const faceCount = indexAttr ? indexAttr.count / 3 : vertexCount / 3;

    return { geometry, vertexCount, faceCount, timeMs };
  }
}

/**
 * Extract a THREE.BufferGeometry from a Manifold object.
 * Expands to non-indexed triangles with flat normals, then applies
 * toCreasedNormals() to smooth normals on curved surfaces while keeping
 * sharp edges (e.g. cube edges, cylinder cap rims).
 * Calls .delete() on the manifold after extraction to free WASM memory.
 */
export function manifoldToGeometry(
  manifold: InstanceType<ManifoldToplevel['Manifold']>,
  deleteAfter = true,
): THREE.BufferGeometry {
  const mesh = manifold.getMesh();
  if (deleteAfter) manifold.delete();

  const numTri = mesh.numTri;
  const numProp = mesh.numProp;
  const vp = mesh.vertProperties;
  const tv = mesh.triVerts;

  // Expand into non-indexed triangles for flat per-face normals
  const totalVerts = numTri * 3;
  const positions = new Float32Array(totalVerts * 3);

  for (let t = 0; t < numTri; t++) {
    for (let v = 0; v < 3; v++) {
      const srcIdx = tv[t * 3 + v];
      const dstIdx = t * 3 + v;
      positions[dstIdx * 3] = vp[srcIdx * numProp];
      positions[dstIdx * 3 + 1] = vp[srcIdx * numProp + 1];
      positions[dstIdx * 3 + 2] = vp[srcIdx * numProp + 2];
    }
  }

  const flat = new THREE.BufferGeometry();
  flat.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  flat.computeVertexNormals();

  // Smooth normals where dihedral angle < 40° (curves), keep sharp edges.
  // Skip for large meshes (>50K triangles) — toCreasedNormals is O(n²) and hangs the browser.
  if (numTri <= 50000) {
    const smoothed = toCreasedNormals(flat, 40 * Math.PI / 180);
    flat.dispose();
    return smoothed;
  }

  return flat;
}
